import { useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { useIntersectionObserver } from '@uidotdev/usehooks';
import WatchLaterOutlinedIcon from '@mui/icons-material/WatchLaterOutlined';
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';

import { TripLegSearchResponseItem } from '@/data/models/trip-leg-search.model';
import { TransportTripLeg } from '@/data/models/transport-reservation.model';
import MemberTripLegLiveTracking from '../reservation/MemberTripLegLiveTracking';
import {
  TripLegStatus,
  tripStatusWithEtaList,
} from '@/data/models/trip-leg-status.model';

import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useDriverLocation } from '@/hooks/useDriverLocation';

import * as lookupSlice from '@/data/lookup.slice';
import { selectTripLegById } from '@/data/slices/trip-leg.slice';
import { selectDriverById } from '@/data/slices/driver.slice';
import { fetchDriverLocationAsync } from '@/data/driver.slice';
import { selectVehicleById } from '@/data/slices/vehicle.slice';

import { useEta } from '@/hooks/display/useEta';
import reservationUtils from '@/services/reservationUtils';

import dateUtils from '@/services/DateUtils';

const PREFIX = 'TripCard';

const classes = {
  tripCardContent: `${PREFIX}-trip-card-content`,
  tripRow: `${PREFIX}-trip-row`,
  tripMapHolder: `${PREFIX}-trip-map-holder`,
  tripMapSpacer: `${PREFIX}-trip-map-spacer`,
  tripMapPlaceholder: `${PREFIX}-trip-map-placeholder`,
  tripMapDropShadow: `${PREFIX}-trip-map-drop-shadow`,
  tripMapOverlay: `${PREFIX}-trip-map-overlay`,
  tripOuterHolder: `${PREFIX}-trip-outer-holder`,
  tripHeaderHolder: `${PREFIX}-trip-header-holder`,
  tripItemHeader: `${PREFIX}-trip-item-header`,
  tripContainer: `${PREFIX}-trip-holder`,
  tripItem: `${PREFIX}-trip-item`,
  tripItemHolder: `${PREFIX}-trip-item-holder`,
  sectionLink: `${PREFIX}-section-link`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.tripCardContent}`]: {
    [`& .section-link`]: {
      textDecoration: 'none',
      color: 'unset',
      pointerEvents: 'cursor',
    },
  },

  [`&.${classes.tripRow}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(3),
    flexWrap: 'wrap',
  },

  [`&.${classes.tripMapHolder}`]: {
    flex: 1,
    display: 'flex',
  },

  [`&.${classes.tripMapDropShadow}`]: {
    filter: `drop-shadow(0px 2px 7px ${theme.palette.lightActionDisabled15p.main})`,
    width: '100%',
    minWidth: '170px',
    maxWidth: '483px',
    position: 'relative',
    '& .MemberTripLegLiveTracking-container .MuiTypography-root': {
      display: 'none',
    },
  },

  [`&.${classes.tripMapSpacer}`]: {
    flex: 1,
    minWidth: 20,
    [theme.breakpoints.up('md')]: {
      minWidth: 50,
    },
    [theme.breakpoints.up('lg')]: {
      minWidth: 75,
    },
    [theme.breakpoints.up('xl')]: {
      minWidth: 100,
    },
  },

  [`&.${classes.tripMapPlaceholder}`]: {
    width: '100%',
    minWidth: '170px',
    maxWidth: '483px',
    height: '233px',
    background: theme.palette.lightActionDisabled15p.main,
  },

  [`&.${classes.tripMapOverlay}`]: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    background: 'transparent',
    top: 0,
    zIndex: 1000,
  },

  [`&.${classes.tripOuterHolder}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.1),
  },

  [`&.${classes.tripContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    [`& .${classes.tripItem}:first-of-type`]: {
      marginTop: theme.spacing(1.5),
    },
  },

  [`&.${classes.tripHeaderHolder}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(1),
    },
  },

  [`& .${classes.tripItemHeader}`]: {
    fontFamily: 'Bitter',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: '18px',
    padding: 0,
  },

  [`&.${classes.tripItem}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1.5),
    [`& svg`]: {
      backgroundColor: 'white',
    },
  },

  [`&.${classes.tripItemHolder}`]: {
    width: '3px',
    height: '100%',
    margin: '0 auto',
    marginTop: theme.spacing(-1.6),
    backgroundImage: "url('/dot-small.svg')",
  },
}));

interface TripCardProps {
  tripLegReferenceID: string;
  legIndex: number;
  interactive?: boolean;
  enableTracking?: boolean;
  fromTripsPage?: boolean;
}

function TripCard({
  tripLegReferenceID,
  legIndex,
  interactive = false,
  enableTracking = false,
  fromTripsPage = false,
}: TripCardProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'));
  const dispatch = useAppDispatch();

  // Pull relevant entities from the new redux adapters
  const tripLeg = useAppSelector((state) =>
    selectTripLegById(state, tripLegReferenceID),
  );
  const driver = useAppSelector((state) =>
    selectDriverById(state, tripLeg?.driverID ?? 0),
  );
  const vehicle = useAppSelector((state) =>
    selectVehicleById(state, tripLeg?.vehicleID ?? 0),
  );

  // Set the currently active trip for the "live" state
  const activeTrip: TripLegSearchResponseItem | null = useMemo(() => {
    if (!tripLeg?.tripLegStatusBC) return null;
    return tripStatusWithEtaList.includes(
      tripLeg.tripLegStatusBC?.toLocaleLowerCase() as TripLegStatus,
    )
      ? tripLeg
      : null;
  }, [tripLeg?.tripLegStatusBC]);

  const timezones = useAppSelector(lookupSlice.selectTimeZones);
  const pickUpAddressTimeZoneIANA = timezones?.find(
    (timezone) => timezone.timeZoneID === tripLeg?.pickUpAddressTimeZoneID,
  )?.ianaStandard;

  const [ref, entry] = useIntersectionObserver({
    threshold: 0,
  });

  const driverLocationForTrip = useDriverLocation({
    driverID: activeTrip?.driverID,
    tripLegID: activeTrip?.tripLegID,
  });

  const driverEtaSeconds = useMemo(() => {
    if (driverLocationForTrip) {
      return driverLocationForTrip.estimatedTimeOfArrivalSeconds;
    }

    const locations = tripLeg?.tripLegDriverLocations;
    if (locations && locations.length > 0) {
      const lastLocation = locations[locations.length - 1];
      return lastLocation?.estimatedTimeOfArrivalSeconds;
    }

    return undefined;
  }, [driverLocationForTrip, tripLeg?.tripLegDriverLocations]);

  const driverEta = useEta(driverEtaSeconds ?? undefined);

  const getPickupDueDateTime = () => {
    if (tripLeg?.pickUpDueTimeUTC) {
      return dateUtils.formatPickupDateTime(
        tripLeg?.pickUpDueTimeUTC!,
        pickUpAddressTimeZoneIANA!,
      );
    } else {
      const pickupDueDate = dateUtils.formatAppointmentDate(
        tripLeg?.appointmentDate,
        pickUpAddressTimeZoneIANA!,
      );
      return `${pickupDueDate}, ${t('member.pickupWillCall')}`;
    }
  };

  // get initial location. Location updates come via realtime events
  useEffect(() => {
    if (!activeTrip?.driverID || !enableTracking) return;

    dispatch(fetchDriverLocationAsync(activeTrip.driverID));
  }, [activeTrip?.driverID, enableTracking]);

  return (
    <>
      {tripLeg && (
        <Card ref={ref}>
          <CardContent>
            <StyledBox className={classes.tripCardContent}>
              <NavLink
                to={`/trip-details/${tripLeg.reservationReferenceID}/${legIndex}?fromTripsPage=${fromTripsPage}`}
                className="section-link">
                <StyledBox className={classes.tripRow}>
                  <StyledBox className={classes.tripOuterHolder}>
                    <StyledBox className={classes.tripHeaderHolder}>
                      <Typography className={classes.tripItemHeader}>
                        {`${t('member.trip')} #${tripLeg.tripLegReferenceID}`}
                      </Typography>
                    </StyledBox>
                    <StyledBox className={classes.tripContainer}>
                      <StyledBox className={classes.tripItem}>
                        <WatchLaterOutlinedIcon color="action" />
                        <StyledBox>
                          {enableTracking && activeTrip ? (
                            <Typography variant="body1" color="green">
                              {t(
                                `member.${activeTrip.tripLegStatusBC.toLocaleLowerCase()}`,
                              )}
                              {driverEta && (
                                <StyledBox component="span" className="bold">
                                  {` - ${t('member.eta')} ${driverEta}`}
                                </StyledBox>
                              )}
                            </Typography>
                          ) : (
                            <Typography variant="body1">
                              {`${t('member.pickup')}: `}
                              <StyledBox component="span" className="bold">
                                {getPickupDueDateTime()}
                              </StyledBox>
                            </Typography>
                          )}
                        </StyledBox>
                      </StyledBox>
                      {(driver || vehicle) && (
                        <StyledBox className={classes.tripItem}>
                          <DirectionsCarFilledOutlinedIcon color="info" />
                          <StyledBox>
                            <Typography variant="body1">
                              {driver && (
                                <StyledBox component="span" className="bold">
                                  {reservationUtils.getDriverName(driver)}
                                </StyledBox>
                              )}
                              {vehicle && (
                                <StyledBox>
                                  {[
                                    vehicle?.vehicleColor,
                                    vehicle?.vehicleMake,
                                    vehicle?.vehicleModel,
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                  {vehicle?.licensePlate &&
                                    ` (${vehicle.licensePlate})`}
                                </StyledBox>
                              )}
                            </Typography>
                            {/* TODO: re-enable provider info after product feedback,
                              based on ticket F1V2-3108 removed it */}
                            {/* {provider?.transportationProviderName && (
                            <Typography variant="body1">
                              {`${t('tripStatusTrack.provider')}: `}
                              <StyledBox component="span" className="bold">
                                {provider.transportationProviderName}
                              </StyledBox>
                            </Typography>
                          )} */}
                          </StyledBox>
                        </StyledBox>
                      )}
                      <StyledBox className={classes.tripItem}>
                        <StyledBox>
                          <RoomOutlinedIcon color="primary" />
                          <StyledBox
                            className={classes.tripItemHolder}></StyledBox>
                        </StyledBox>
                        <StyledBox>
                          <Typography variant="body1">
                            {`${t('member.from')}: `}

                            <Box component="span" className="bold">
                              {tripLeg.pickUpAddressLine1}
                            </Box>
                          </Typography>
                        </StyledBox>
                      </StyledBox>
                      <StyledBox className={classes.tripItem}>
                        <RoomOutlinedIcon color="error" />
                        <StyledBox>
                          <Typography variant="body1">
                            {`${t('member.to_titleCase')}: `}
                            <StyledBox component="span" className="bold">
                              {tripLeg.dropOffAddressLine1}
                            </StyledBox>
                          </Typography>
                        </StyledBox>
                      </StyledBox>
                    </StyledBox>
                  </StyledBox>
                  {isLarge && (
                    <StyledBox className={classes.tripMapHolder}>
                      <StyledBox className={classes.tripMapSpacer} />
                      <StyledBox className={classes.tripMapDropShadow}>
                        {entry?.isIntersecting ? (
                          <MemberTripLegLiveTracking
                            tripLeg={tripLeg as unknown as TransportTripLeg}
                          />
                        ) : (
                          <StyledBox className={classes.tripMapPlaceholder} />
                        )}
                        {!interactive && (
                          <StyledBox
                            className={classes.tripMapOverlay}></StyledBox>
                        )}
                      </StyledBox>
                    </StyledBox>
                  )}
                </StyledBox>
              </NavLink>
            </StyledBox>
          </CardContent>
        </Card>
      )}
    </>
  );
}

export default TripCard;
